import React, { useReducer, useContext, useRef, useCallback, useEffect, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { firmwareReducer } from './firmwareReducer';
import { FirmwareActionTypes } from './firmwareActions';
import {
	firmwareInitialState,
	FirmwareContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './firmwareContext';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedFirmware, deleteFirmware } from 'backend/firmware';
import { AxiosResponse } from 'axios';
import {
	IDeleteApkFirmwareValidationError,
	IServerError,
	ServerErrorTypes,
	IFirmware,
	IGetPaginatedFirmware,
	TFirmwareType,
} from 'types';
import axios from 'axios';

const FirmwareState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(firmwareReducer, firmwareInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IFirmware>, IGetPaginatedFirmware>(
		isMounted.current ? getPaginatedFirmware : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			type: state.IDSelectedTypeFirmware,
			isUnassignedFirmware: state.isUnassignedFirmware,
			sortName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortVersion: state.sortBy === 'VersionA-Z' || state.sortBy === 'VersionZ-A' ? state.sortBy : null,
			sortDescription:
				state.sortBy === 'DescriptionA-Z' || state.sortBy === 'DescriptionZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: FirmwareActionTypes.SET_FIRMWARE,
				payload: {
					firmware: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteFirmware(state.IDForDeletion);
			const filteredFirmware = state.firmware.items.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: FirmwareActionTypes.SET_FIRMWARE,
				payload: {
					firmware: {
						count: state.firmware.count - 1,
						items: filteredFirmware,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			if (axios.isAxiosError(err) && err.response?.data.Type === ServerErrorTypes.FIRMWARE_DELETION_FAILED) {
				dispatch({
					type: FirmwareActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
					payload: {
						errorResponseData: err.response.data,
						typeOfModalWindow: null,
					},
				});
			} else {
				dispatch({
					type: FirmwareActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'error',
					},
				});
			}
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: FirmwareActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const changeTypeFirmware = (value: Nullable<TFirmwareType>) => {
		dispatch({
			type: FirmwareActionTypes.CHANGE_TYPE_FIRMWARE,
			payload: {
				IDSelectedTypeFirmware: value,
			},
		});
	};

	const toggleUnassignedFirmware = () => {
		dispatch({ type: FirmwareActionTypes.TOGGLE_UNASSIGNED_FIRMWARE });
	};

	const setSortBy = (sortBy: TSortBy) => {
		dispatch({
			type: FirmwareActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: FirmwareActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: FirmwareActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: FirmwareActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: FirmwareActionTypes.RESET_SELECTED_CONTROLS });

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: FirmwareActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const showErrorModalWithInfo = useCallback((value: IServerError<IDeleteApkFirmwareValidationError> | null) => {
		dispatch({
			type: FirmwareActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
				typeOfModalWindow: null,
			},
		});
	}, []);

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: FirmwareActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<FirmwareContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				changeTypeFirmware,
				toggleUnassignedFirmware,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				resetSelectedControls,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				showErrorModalWithInfo,
				firmware: state.firmware,
				searchString: state.searchString,
				IDSelectedTypeFirmware: state.IDSelectedTypeFirmware,
				isUnassignedFirmware: state.isUnassignedFirmware,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</FirmwareContext.Provider>
	);
};

export default FirmwareState;
